/**
 * Error Handler Utility
 * Centralized error handling and user notification system
 */

import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { AxiosError } from 'axios'
import { ApiError } from '../services/api'

const MySwal = withReactContent(Swal)

/**
 * Error types for categorization
 */
export enum ErrorType {
	NETWORK = 'NETWORK',
	VALIDATION = 'VALIDATION',
	AUTHENTICATION = 'AUTHENTICATION',
	AUTHORIZATION = 'AUTHORIZATION',
	NOT_FOUND = 'NOT_FOUND',
	SERVER = 'SERVER',
	UNKNOWN = 'UNKNOWN',
}

/**
 * User-friendly error messages in Greek
 */
const ERROR_MESSAGES: Record<ErrorType, string> = {
	[ErrorType.NETWORK]: 'Παρουσιάστηκε πρόβλημα σύνδεσης. Παρακαλώ ελέγξτε τη σύνδεσή σας στο διαδίκτυο.',
	[ErrorType.VALIDATION]: 'Τα δεδομένα που εισάγατε δεν είναι έγκυρα. Παρακαλώ ελέγξτε τα πεδία.',
	[ErrorType.AUTHENTICATION]: 'Η συνεδρία σας έχει λήξει. Παρακαλώ συνδεθείτε ξανά.',
	[ErrorType.AUTHORIZATION]: 'Δεν έχετε δικαίωμα πρόσβασης σε αυτή την ενέργεια.',
	[ErrorType.NOT_FOUND]: 'Το στοιχείο που ζητήσατε δεν βρέθηκε.',
	[ErrorType.SERVER]: 'Παρουσιάστηκε πρόβλημα στον διακομιστή. Παρακαλώ δοκιμάστε ξανά αργότερα.',
	[ErrorType.UNKNOWN]: 'Παρουσιάστηκε ένα απροσδόκητο σφάλμα. Παρακαλώ δοκιμάστε ξανά.',
}

/**
 * Categorize error based on its type
 */
export function categorizeError(error: unknown): ErrorType {
	if (error instanceof AxiosError) {
		if (!error.response) {
			return ErrorType.NETWORK
		}

		const status = error.response.status
		if (status === 400) return ErrorType.VALIDATION
		if (status === 401) return ErrorType.AUTHENTICATION
		if (status === 403) return ErrorType.AUTHORIZATION
		if (status === 404) return ErrorType.NOT_FOUND
		if (status >= 500) return ErrorType.SERVER

		return ErrorType.UNKNOWN
	}

	if (error instanceof ApiError) {
		if (error.code === 'ERR_NETWORK') return ErrorType.NETWORK
		if (error.status === 400) return ErrorType.VALIDATION
		if (error.status === 401) return ErrorType.AUTHENTICATION
		if (error.status === 403) return ErrorType.AUTHORIZATION
		if (error.status === 404) return ErrorType.NOT_FOUND
		if (error.status >= 500) return ErrorType.SERVER
	}

	return ErrorType.UNKNOWN
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: unknown, customMessage?: string): string {
	if (customMessage) return customMessage

	// If error has a message property, try to use it
	if (error instanceof Error && error.message) {
		// If it's a Greek message from the API, use it
		if (/[\u0370-\u03FF]/.test(error.message)) {
			return error.message
		}
	}

	// Otherwise, use categorized message
	const errorType = categorizeError(error)
	return ERROR_MESSAGES[errorType]
}

/**
 * Show error notification to user
 */
export function showErrorNotification(error: unknown, customMessage?: string): void {
	const message = getErrorMessage(error, customMessage)

	MySwal.fire({
		icon: 'error',
		title: 'Σφάλμα',
		text: message,
		confirmButtonText: 'Εντάξει',
	})
}

/**
 * Show success notification to user
 */
export function showSuccessNotification(message: string, title: string = 'Επιτυχία'): void {
	MySwal.fire({
		icon: 'success',
		title,
		text: message,
		timer: 2000,
		showConfirmButton: false,
	})
}

/**
 * Show info notification to user
 */
export function showInfoNotification(message: string, title: string = 'Πληροφορία'): void {
	MySwal.fire({
		icon: 'info',
		title,
		text: message,
		confirmButtonText: 'Εντάξει',
	})
}

/**
 * Show warning notification to user
 */
export function showWarningNotification(message: string, title: string = 'Προσοχή'): void {
	MySwal.fire({
		icon: 'warning',
		title,
		text: message,
		confirmButtonText: 'Εντάξει',
	})
}

/**
 * Show confirmation dialog
 */
export async function showConfirmDialog(message: string, title: string = 'Επιβεβαίωση'): Promise<boolean> {
	const result = await MySwal.fire({
		icon: 'question',
		title,
		text: message,
		showCancelButton: true,
		confirmButtonText: 'Ναι',
		cancelButtonText: 'Όχι',
		confirmButtonColor: '#3085d6',
		cancelButtonColor: '#d33',
	})

	return result.isConfirmed
}

/**
 * Log error for debugging (in development only)
 */
export function logError(error: unknown, context?: string): void {
	if (process.env.NODE_ENV === 'development') {
		console.error(`[Error${context ? ` in ${context}` : ''}]:`, error)
	}
}

/**
 * Handle error with logging and notification
 */
export function handleError(error: unknown, context?: string, customMessage?: string): void {
	logError(error, context)
	showErrorNotification(error, customMessage)
}





